import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import withTranslation from 'hoc/withTranslation';
import config from 'config';
import { colors, fontSize } from '../../../global/variables';
import { getFleetTypeIcon } from '../../../utils/FleetHelpers';
import { isProdEnv } from '../../../utils/helpers';
import editIcon from '../../../global/img/edit-dark.svg';
import deleteIcon from '../../../global/img/delete.svg';
import FleetProfileEditor from './FleetProfileEditor';
import FleetTypeEditor from './FleetTypeEditor';

const {
  secondaryTextColor,
  primaryTextColor,
  primaryBorderColor,
  secondaryBorderColor,
  aqua,
  darkBackground,
} = colors;
const { small, smaller, normal } = fontSize;

const isProd = isProdEnv();
const {
  documentationLinks: { documentationTP },
} = config;

const WIZARD_PANEL_OFFSET = '28rem';
const MAX_PROFILES = 5;

const StyledOverlay = styled.div({
  position: 'fixed',
  top: '4rem',
  bottom: '2rem',
  left: WIZARD_PANEL_OFFSET,
  right: '1rem',
  backgroundColor: darkBackground,
  zIndex: 10200,
  display: 'flex',
  alignItems: 'stretch',
  borderRadius: '0.25rem',
  overflow: 'hidden',
});

const StyledModalBox = styled.div({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  borderRadius: '0.12rem',
  textAlign: 'left',
  overflow: 'hidden',
});

const StyledHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.6rem',
  padding: '0.7rem 1rem',
  borderBottom: `1px solid ${secondaryBorderColor}`,
  flexShrink: 0,
});

const StyledHeaderTitle = styled.div({
  fontSize: normal,
  fontWeight: 500,
  color: primaryTextColor,
  flex: 1,
});

const StyledPillButton = styled.button(({ disabled }) => ({
  background: 'transparent',
  color: primaryTextColor,
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '1.5rem',
  padding: '0.3rem 0.75rem',
  fontSize: smaller,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontFamily: 'FiraGO',
  cursor: disabled ? 'not-allowed' : 'pointer',
  whiteSpace: 'nowrap',
  opacity: disabled ? 0.5 : 1,
  transition: 'all 250ms cubic-bezier(0.365, 0.005, 0.285, 1.005)',
  ':hover': {
    borderColor: disabled ? primaryBorderColor : aqua,
    color: disabled ? primaryTextColor : aqua,
  },
}));

const StyledPillButtonFull = styled(StyledPillButton)({
  width: '100%',
  justifyContent: 'center',
  marginBottom: '0.5rem',
});

const StyledCloseButton = styled(StyledPillButton)({
  borderRadius: '0.12rem',
});

const StyledBody = styled.div({
  flex: 1,
  display: 'grid',
  gridTemplateColumns: '18rem 1fr',
  overflow: 'hidden',
});

const StyledList = styled.div({
  borderRight: `1px solid ${secondaryBorderColor}`,
  padding: '0.85rem',
  overflowY: 'auto',
});

const StyledSectionHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '0.5rem',
});

const StyledSectionTitle = styled.span({
  fontSize: smaller,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 700,
  color: secondaryTextColor,
});

const StyledSectionBadge = styled.span({
  fontSize: smaller,
  fontWeight: 700,
  backgroundColor: 'rgba(0,0,0,0.03)',
  border: `1px solid ${secondaryBorderColor}`,
  borderRadius: '1.5rem',
  padding: '0.05rem 0.45rem',
  color: secondaryTextColor,
});

const StyledCard = styled.div(({ editing, clickable }) => ({
  border: `1px solid ${editing ? aqua : primaryBorderColor}`,
  backgroundColor: editing ? 'rgba(34,205,200,.05)' : 'transparent',
  borderRadius: '0.12rem',
  padding: '0.55rem 0.65rem',
  marginBottom: '0.6rem',
  cursor: clickable ? 'pointer' : 'default',
}));

const StyledPrototypeCard = styled.div({
  border: `1.5px dashed ${aqua}`,
  backgroundColor: 'rgba(34,205,200,.08)',
  borderRadius: '0.12rem',
  padding: '0.55rem 0.65rem',
  marginBottom: '0.6rem',
});

const StyledCardRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
});

const StyledCardIcon = styled.div(({ iconUrl, dimmed }) => ({
  width: '1.1rem',
  height: '1.1rem',
  flexShrink: 0,
  backgroundImage: `url(${iconUrl})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  opacity: dimmed ? 0.5 : 1,
}));

const StyledCardName = styled.span(({ dimmed }) => ({
  fontWeight: 500,
  fontSize: normal,
  color: primaryTextColor,
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  opacity: dimmed ? 0.5 : 1,
}));

const StyledPrototypeName = styled.span(({ isPlaceholder }) => ({
  fontWeight: isPlaceholder ? 400 : 500,
  fontStyle: isPlaceholder ? 'italic' : 'normal',
  fontSize: normal,
  color: aqua,
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const StyledBadge = styled.span({
  fontSize: smaller,
  fontWeight: 500,
  color: aqua,
  border: `1px solid ${aqua}`,
  borderRadius: '1.5rem',
  padding: '0.05rem 0.45rem',
  flexShrink: 0,
});

const StyledCardActionIcon = styled.button(({ iconUrl, variant, disabled }) => ({
  width: '1.5rem',
  height: '1.5rem',
  flexShrink: 0,
  border: `1px solid ${variant === 'delete' ? 'rgba(255,102,102,.5)' : aqua}`,
  borderRadius: '0.12rem',
  background: '#fff',
  backgroundImage: `url(${iconUrl})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: '0.8rem',
  backgroundPosition: 'center',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  ':hover': {
    backgroundColor: disabled
      ? '#fff'
      : variant === 'delete'
      ? 'rgba(255,102,102,.12)'
      : 'rgba(34,205,200,.12)',
  },
}));

const StyledCardCheckbox = styled.label({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  fontSize: smaller,
  color: secondaryTextColor,
  cursor: 'pointer',
  flexShrink: 0,
  'input[type="checkbox"]': {
    display: 'inline-block',
    visibility: 'visible',
    width: 'auto',
    height: 'auto',
    cursor: 'pointer',
  },
});

const StyledCardMeta = styled.div(({ dimmed }) => ({
  fontSize: smaller,
  color: secondaryTextColor,
  marginTop: '0.3rem',
  paddingLeft: '1.5rem',
  opacity: dimmed ? 0.5 : 1,
}));

const StyledEmptyState = styled.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: '0.6rem',
  padding: '2rem',
});

const StyledEmptyTitle = styled.div({
  fontSize: normal,
  fontWeight: 500,
  color: primaryTextColor,
});

const StyledEmptyDescription = styled.p({
  fontSize: small,
  color: secondaryTextColor,
  maxWidth: '18rem',
});

const StyledEmptyActions = styled.div({
  display: 'flex',
  gap: '0.5rem',
});

const StyledToggleAllWrapper = styled.label({
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  fontSize: smaller,
  color: secondaryTextColor,
  cursor: 'pointer',
  marginBottom: '0.75rem',
  'input[type="checkbox"]': {
    display: 'inline-block',
    visibility: 'visible',
    width: 'auto',
    height: 'auto',
    cursor: 'pointer',
  },
});

const StyledBackButton = styled.button({
  background: 'transparent',
  border: 'none',
  color: aqua,
  fontSize: small,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'FiraGO',
  padding: 0,
});

const FleetManagerModal = ({
  profiles,
  types,
  initialMode,
  initialTypeIndex,
  oAuth,
  apiKey,
  canFleetTypeBeElectric,
  onClose,
  onSaveProfile,
  onSaveType,
  onToggleType,
  onToggleAllTypes,
  onDeleteProfile,
  onDeleteType,
  translations: {
    wizard: {
      fleet: {
        fleetTypes,
        fleetManagerTitle,
        addProfileLabel,
        addTypeLabel,
        closeLabel,
        profilesTitle,
        typesTitle,
        addProfileFullLabel,
        addTypeFullLabel,
        deleteProfileTooltip,
        deleteProfileInUseTooltip,
        deleteTypeTooltip,
        enabledLabel,
        disabledLabel,
        selectAllLabel,
        unselectAllLabel,
        evLabel,
        selectCardTitle,
        selectCardDescription,
        editTypeCta,
        editProfileCta,
        editingProfileTitle,
        editingTypeTitle,
        addingProfileTitle,
        addingTypeTitle,
        newProfilePlaceholder,
        newTypePlaceholder,
        backToFleetLabel,
        profileNameLabel,
        profileTypeLabel,
        electricVehicleLabel,
        electricVehicleInfoTooltip,
        evAuthErrorMessage,
        evAuthErrorContactUs,
        electricVehicleProfileSectionTitle,
        saveProfileLabel,
        cancelProfileLabel,
        profileNamePlaceholder,
        typeNameLabel,
        typeIdLabel,
        linkedProfileLabel,
        typeAmountLabel,
        typeCapacityLabel,
        costFixedLabel,
        costTimeLabel,
        costDistanceLabel,
        saveTypeLabel,
        cancelTypeLabel,
        typeNamePlaceholder,
        typeIdPlaceholder,
      },
    },
  },
}) => {
  const [panelMode, setPanelMode] = useState(initialMode || 'list');
  const [selectedProfileIndex, setSelectedProfileIndex] = useState(null);
  const [selectedTypeIndex, setSelectedTypeIndex] = useState(
    initialMode === 'editType' ? initialTypeIndex : null,
  );
  const [draftPreview, setDraftPreview] = useState({ name: '', fleetType: 'car' });

  const onEditProfileClick = useCallback((index) => {
    setSelectedProfileIndex(index);
    setPanelMode('editProfile');
  }, []);

  const onAddProfileClick = useCallback(() => {
    if (profiles.length >= MAX_PROFILES) return;
    setSelectedProfileIndex(null);
    setDraftPreview({ name: '', fleetType: 'car' });
    setPanelMode('addProfile');
  }, [profiles.length]);

  const onEditTypeClick = useCallback((index) => {
    setSelectedTypeIndex(index);
    setPanelMode('editType');
  }, []);

  const onAddTypeClick = useCallback(() => {
    setSelectedTypeIndex(null);
    setDraftPreview({ name: '', fleetType: 'car' });
    setPanelMode('addType');
  }, []);

  const onCancelPanel = useCallback(() => setPanelMode('list'), []);

  const onDraftChange = useCallback((draft) => setDraftPreview(draft), []);

  const onSaveProfileClick = useCallback(
    (draft) => {
      onSaveProfile(selectedProfileIndex, draft);
      setPanelMode('list');
    },
    [onSaveProfile, selectedProfileIndex],
  );

  const onSaveTypeClick = useCallback(
    (draft) => {
      onSaveType(selectedTypeIndex, draft);
      setPanelMode('list');
    },
    [onSaveType, selectedTypeIndex],
  );

  const isEditingProfile = panelMode === 'editProfile';
  const isAddingProfile = panelMode === 'addProfile';
  const isEditingType = panelMode === 'editType';
  const isAddingType = panelMode === 'addType';
  const isShowingProfileEditor = isEditingProfile || isAddingProfile;
  const isShowingTypeEditor = isEditingType || isAddingType;
  const hasReachedProfileLimit = profiles.length >= MAX_PROFILES;
  const currentProfile = selectedProfileIndex !== null ? profiles[selectedProfileIndex] : null;
  const currentType = selectedTypeIndex !== null ? types[selectedTypeIndex] : null;
  const currentTypeProfile =
    (currentType && profiles.find(({ name }) => name === currentType.profile)) || {};
  const headerFleetTypeIconUrl = getFleetTypeIcon(profiles[0]?.fleetType);

  const enabledCount = types.filter((t) => t.enabled !== false).length;
  const allTypesSelected = enabledCount === types.length;
  const someTypesSelected = enabledCount > 0 && !allTypesSelected;
  const toggleAllRef = useRef(null);

  useEffect(() => {
    if (toggleAllRef.current) {
      toggleAllRef.current.indeterminate = someTypesSelected;
    }
  }, [someTypesSelected]);

  const onToggleAllClick = useCallback(() => {
    onToggleAllTypes(!allTypesSelected);
  }, [onToggleAllTypes, allTypesSelected]);

  return createPortal(
    <StyledOverlay>
      <StyledModalBox>
        <StyledHeader>
          {isShowingProfileEditor && (
            <>
              <StyledBackButton id="button-fleet-manager-back" onClick={onCancelPanel}>
                {backToFleetLabel}
              </StyledBackButton>
              <StyledHeaderTitle>
                {isAddingProfile
                  ? addingProfileTitle
                  : `${editingProfileTitle} — ${currentProfile?.name}`}
              </StyledHeaderTitle>
            </>
          )}
          {isShowingTypeEditor && (
            <>
              <StyledBackButton id="button-fleet-manager-back" onClick={onCancelPanel}>
                {backToFleetLabel}
              </StyledBackButton>
              <StyledHeaderTitle>
                {isAddingType ? addingTypeTitle : `${editingTypeTitle} — ${currentType?.name}`}
              </StyledHeaderTitle>
            </>
          )}
          {panelMode === 'list' && (
            <>
              <StyledCardIcon iconUrl={headerFleetTypeIconUrl} />
              <StyledHeaderTitle>{fleetManagerTitle}</StyledHeaderTitle>
              <StyledPillButton
                id="button-fleet-manager-add-profile"
                disabled={hasReachedProfileLimit}
                onClick={onAddProfileClick}
              >
                {addProfileLabel}
              </StyledPillButton>
              <StyledPillButton id="button-fleet-manager-add-type" onClick={onAddTypeClick}>
                {addTypeLabel}
              </StyledPillButton>
            </>
          )}
          <StyledCloseButton id="button-fleet-manager-close" onClick={onClose}>
            {closeLabel}
          </StyledCloseButton>
        </StyledHeader>
        <StyledBody>
          <StyledList>
            <StyledSectionHeader>
              <StyledSectionTitle>{profilesTitle}</StyledSectionTitle>
              <StyledSectionBadge>{profiles.length}</StyledSectionBadge>
            </StyledSectionHeader>
            <StyledPillButtonFull
              id="button-fleet-manager-add-profile-full"
              disabled={hasReachedProfileLimit}
              onClick={onAddProfileClick}
            >
              {addProfileFullLabel}
            </StyledPillButtonFull>
            {profiles.map((profile, index) => {
              const isProfileInUse = types.some((t) => t.profile === profile.name);
              const isDeleteProfileDisabled = profiles.length <= 1 || isProfileInUse;
              return (
                <StyledCard
                  key={profile.name}
                  editing={isEditingProfile && selectedProfileIndex === index}
                  clickable
                  onClick={() => onEditProfileClick(index)}
                >
                  <StyledCardRow>
                    <StyledCardIcon iconUrl={getFleetTypeIcon(profile.fleetType)} />
                    <StyledCardName>{profile.name}</StyledCardName>
                    {profile.isElectricVehicle && <StyledBadge>{evLabel}</StyledBadge>}
                    <StyledCardActionIcon
                      id={`button-fleet-manager-edit-profile-${index}`}
                      iconUrl={editIcon}
                      onClick={() => onEditProfileClick(index)}
                    />
                    <StyledCardActionIcon
                      id={`button-fleet-manager-delete-profile-${index}`}
                      variant="delete"
                      iconUrl={deleteIcon}
                      title={isProfileInUse ? deleteProfileInUseTooltip : deleteProfileTooltip}
                      disabled={isDeleteProfileDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isDeleteProfileDisabled) onDeleteProfile(index);
                      }}
                    />
                  </StyledCardRow>
                </StyledCard>
              );
            })}
            {isAddingProfile && (
              <StyledPrototypeCard>
                <StyledCardRow>
                  <StyledCardIcon iconUrl={getFleetTypeIcon(draftPreview.fleetType || 'car')} />
                  <StyledPrototypeName isPlaceholder={!draftPreview.name}>
                    {draftPreview.name || newProfilePlaceholder}
                  </StyledPrototypeName>
                </StyledCardRow>
              </StyledPrototypeCard>
            )}
            <StyledSectionHeader>
              <StyledSectionTitle>{typesTitle}</StyledSectionTitle>
              <StyledSectionBadge>{types.length}</StyledSectionBadge>
            </StyledSectionHeader>
            <StyledToggleAllWrapper htmlFor="checkbox-fleet-type-toggle-all">
              <input
                ref={toggleAllRef}
                id="checkbox-fleet-type-toggle-all"
                type="checkbox"
                checked={allTypesSelected}
                onChange={onToggleAllClick}
              />
              {allTypesSelected ? unselectAllLabel : selectAllLabel}
            </StyledToggleAllWrapper>
            <StyledPillButtonFull id="button-fleet-manager-add-type-full" onClick={onAddTypeClick}>
              {addTypeFullLabel}
            </StyledPillButtonFull>
            {types
              .map((type, index) => ({ type, index }))
              .sort((a, b) => (b.type.enabled !== false) - (a.type.enabled !== false))
              .map(({ type, index }) => {
                const linkedProfile = profiles.find(({ name }) => name === type.profile) || {};
                const isTypeEnabled = type.enabled !== false;
                return (
                  <StyledCard
                    key={type.id || index}
                    editing={isEditingType && selectedTypeIndex === index}
                    clickable
                    onClick={() => onEditTypeClick(index)}
                  >
                    <StyledCardRow>
                      <StyledCardIcon
                        iconUrl={getFleetTypeIcon(linkedProfile.fleetType)}
                        dimmed={!isTypeEnabled}
                      />
                      <StyledCardName dimmed={!isTypeEnabled}>
                        {type.name || type.id}
                      </StyledCardName>
                      <StyledCardCheckbox onClick={(e) => e.stopPropagation()}>
                        <input
                          id={`checkbox-fleet-type-enabled-${index}`}
                          type="checkbox"
                          checked={isTypeEnabled}
                          onChange={() => onToggleType(index)}
                        />
                        {isTypeEnabled ? enabledLabel : disabledLabel}
                      </StyledCardCheckbox>
                      <StyledCardActionIcon
                        id={`button-fleet-manager-edit-type-${index}`}
                        iconUrl={editIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditTypeClick(index);
                        }}
                      />
                      <StyledCardActionIcon
                        id={`button-fleet-manager-delete-type-${index}`}
                        variant="delete"
                        iconUrl={deleteIcon}
                        title={deleteTypeTooltip}
                        disabled={types.length <= 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (types.length > 1) onDeleteType(index);
                        }}
                      />
                    </StyledCardRow>
                    <StyledCardMeta dimmed={!isTypeEnabled}>
                      {linkedProfile.name} · {type.amount}
                    </StyledCardMeta>
                  </StyledCard>
                );
              })}
            {isAddingType && (
              <StyledPrototypeCard>
                <StyledCardRow>
                  <StyledCardIcon iconUrl={getFleetTypeIcon(draftPreview.fleetType || 'car')} />
                  <StyledPrototypeName isPlaceholder={!draftPreview.name}>
                    {draftPreview.name || newTypePlaceholder}
                  </StyledPrototypeName>
                </StyledCardRow>
              </StyledPrototypeCard>
            )}
          </StyledList>
          {isShowingProfileEditor && (
            <FleetProfileEditor
              key={isAddingProfile ? 'new-profile' : selectedProfileIndex}
              isNew={isAddingProfile}
              profileName={currentProfile?.name}
              fleetType={currentProfile?.fleetType}
              isElectricVehicle={currentProfile?.isElectricVehicle}
              evProfile={currentProfile?.evProfile}
              oAuth={oAuth}
              apiKey={apiKey}
              vehicleAmount={currentType?.amount}
              fleetTypeOptions={fleetTypes}
              canFleetTypeBeElectric={canFleetTypeBeElectric}
              showEvInfo={isProd}
              onSave={onSaveProfileClick}
              onCancel={onCancelPanel}
              onDraftChange={isAddingProfile ? onDraftChange : undefined}
              labels={{
                profileNameLabel,
                profileTypeLabel,
                electricVehicleLabel,
                electricVehicleInfoTooltip,
                evAuthErrorMessage,
                evAuthErrorContactUs,
                evContactLink: documentationTP,
                electricVehicleProfileSectionTitle,
                saveProfileLabel,
                cancelProfileLabel,
                profileNamePlaceholder,
              }}
            />
          )}
          {isShowingTypeEditor && (
            <FleetTypeEditor
              key={isAddingType ? 'new-type' : `${selectedTypeIndex}-${currentType?.amount}`}
              isNew={isAddingType}
              typeName={currentType?.name}
              typeId={currentType?.id}
              profileName={currentTypeProfile?.name}
              profiles={profiles}
              amount={currentType?.amount}
              capacity={currentType?.capacity?.[0]}
              costs={currentType?.costs}
              onSave={onSaveTypeClick}
              onCancel={onCancelPanel}
              onDraftChange={isAddingType ? onDraftChange : undefined}
              labels={{
                typeNameLabel,
                typeIdLabel,
                linkedProfileLabel,
                typeAmountLabel,
                typeCapacityLabel,
                costFixedLabel,
                costTimeLabel,
                costDistanceLabel,
                saveTypeLabel,
                cancelTypeLabel,
                typeNamePlaceholder,
                typeIdPlaceholder,
              }}
            />
          )}
          {panelMode === 'list' && (
            <StyledEmptyState>
              <StyledEmptyTitle>{selectCardTitle}</StyledEmptyTitle>
              <StyledEmptyDescription>{selectCardDescription}</StyledEmptyDescription>
              <StyledEmptyActions>
                <StyledPillButton
                  id="button-fleet-manager-edit-a-type"
                  onClick={() => types.length > 0 && onEditTypeClick(0)}
                >
                  {editTypeCta}
                </StyledPillButton>
                <StyledPillButton
                  id="button-fleet-manager-edit-a-profile"
                  onClick={() => profiles.length > 0 && onEditProfileClick(0)}
                >
                  {editProfileCta}
                </StyledPillButton>
              </StyledEmptyActions>
            </StyledEmptyState>
          )}
        </StyledBody>
      </StyledModalBox>
    </StyledOverlay>,
    document.body,
  );
};

export default withTranslation(FleetManagerModal);
